import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { requestReturn, requestExchange } from "../services/orderService";
import { verifyBankAccount } from "../services/paymentService";
import sizeCharts from "../data/sizeCharts.json";
import { showErrorToast } from "../utils/errorHandler";

const ReturnExchangeSidebar = ({
  isOpen,
  onClose,
  orderItem,
  orderId,
  onSuccess,
}) => {
  const [step, setStep] = useState("initial");
  const [returnReason, setReturnReason] = useState("");
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [exchangeSize, setExchangeSize] = useState("");
  const [exchangeQuantity, setExchangeQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    ifscCode: "",
    bankName: "",
    accountHolderName: "",
  });
  const [bankVerifying, setBankVerifying] = useState(false);
  const [bankError, setBankError] = useState("");
  const [isCODOrder, setIsCODOrder] = useState(false);

  const item = orderItem || {};
  const maxQuantity = Math.max(1, Number(item.quantity || 1));

  const allSizes = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];
  const currentSize = String(item.size || "")
    .toUpperCase()
    .trim();

  const availableSizes = useMemo(() => {
    const filtered = allSizes.filter((size) => size !== currentSize);
    return filtered.length ? filtered : allSizes;
  }, [currentSize]);

  const itemImage =
    item.image ||
    item.productImage ||
    item.images?.[0] ||
    item.product?.images?.[0] ||
    "";

  const itemName = item.productName || item.name || "Product";
  const itemSize = item.size || "One Size";
  const itemPrice = Number(item.price || item.discountedPrice || 0);

  const isBottomForChart = useMemo(() => {
    const source = `${item?.productType || ""} ${itemName}`.toLowerCase();
    return /(pant|short|skirt|bottom|trouser|jean)/.test(source);
  }, [item?.productType, itemName]);

  const sizeChartData = isBottomForChart ? sizeCharts.bottoms : sizeCharts.tops;

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep("initial");
      setReturnReason("");
      setReturnQuantity(1);
      setExchangeSize("");
      setExchangeQuantity(1);
      setLoading(false);
      setShowSizeGuide(false);
      setBankDetails({ accountNumber: "", ifscCode: "", bankName: "", accountHolderName: "" });
      setBankError("");
      setIsCODOrder(false);
    } else {
      // Check if order is COD
      setIsCODOrder(orderItem?.paymentMethod === 'COD');
    }
  }, [isOpen, orderItem]);

  const handleReturn = async () => {
    if (!returnReason) {
      toast.error("Please tell us what went wrong");
      return;
    }

    // For COD orders, validate bank details
    if (isCODOrder) {
      if (!bankDetails.accountNumber || !bankDetails.ifscCode || !bankDetails.accountHolderName) {
        toast.error("Please provide all bank details for refund");
        return;
      }
      if (bankError) {
        toast.error("Please verify your bank account details");
        return;
      }
    }

    setLoading(true);
    try {
      const itemId = item._id || item.product;
      await requestReturn(
        orderId,
        itemId,
        returnReason,
        returnQuantity,
        isCODOrder ? bankDetails : undefined
      );
      toast.success("Return request placed successfully");
      if (onSuccess) onSuccess();
      onClose();
      setStep("initial");
    } catch (err) {
      showErrorToast(err, "Failed to place return request");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyBank = async () => {
    if (!bankDetails.ifscCode || bankDetails.ifscCode.length < 11) {
      setBankError("Invalid IFSC code");
      return;
    }

    setBankVerifying(true);
    setBankError("");

    try {
      const result = await verifyBankAccount({
        ifscCode: bankDetails.ifscCode,
        accountNumber: bankDetails.accountNumber,
        accountHolderName: bankDetails.accountHolderName,
      });

      if (result.valid) {
        setBankDetails(prev => ({
          ...prev,
          bankName: result.bankName || '',
        }));
        setBankError("");
      } else {
        setBankError(result.error || 'Bank verification failed');
      }
    } catch (err) {
      setBankError('Unable to verify bank account. Please check your details.');
      console.error('Bank verification error:', err);
    } finally {
      setBankVerifying(false);
    }
  };

  const handleExchange = async () => {
    if (!exchangeSize) {
      toast.error("Please select a new size");
      return;
    }
    setLoading(true);
    try {
      const itemId = item._id || item.product;
      const exchangeReason =
        exchangeQuantity > 1
          ? `Size exchange (Qty: ${exchangeQuantity})`
          : "Size exchange";
      await requestExchange(orderId, itemId, exchangeReason, exchangeSize);
      toast.success("Exchange request placed successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showErrorToast(err, "Failed to place exchange request");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  const getTitle = () => {
    if (step === "return") return "Return";
    if (step === "exchange") return "Exchange";
    return "Return/Exchange";
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/45 z-[999]"
        onClick={handleClose}
      ></div>
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-[100vw] sm:max-w-lg h-screen bg-[#f2f2f2] shadow-xl z-[1000] font-bdogrotesk flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-2 sm:px-3 py-2">
          <h2 className="text-base font-medium text-black">{getTitle()}</h2>
          <button
            className="text-base font-medium text-black"
            onClick={handleClose}
          >
            Close
          </button>
        </div>

        <div className="px-2 sm:px-3 pb-2 flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col">
            {/* Initial Step - Return or Exchange */}
            {step === "initial" && (
              <>
                <div className="mb-6">
                  <h3 className="text-[26px] leading-[1.05] sm:text-4xl font-medium text-black">
                    We want Nufab pieces to be your go-to. If this isn't "The
                    One", send it back and let's find you something that is!
                  </h3>
                </div>

                {/* Product Display */}
                <div className="flex gap-2 mb-6">
                  <div className="w-32 h-40 sm:w-48 sm:h-48 bg-gray-100 flex-shrink-0">
                    {itemImage && (
                      <img
                        src={itemImage}
                        alt={itemName}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex flex-col justify-between flex-1 min-w-0">
                    <div className="w-full">
                      <div className="flex w-full items-start justify-between">
                        <h4 className="text-[13px] sm:text-[14px] leading-tight font-medium text-black flex-1 pr-2 sm:pr-8">
                          {itemName} - {itemSize}
                        </h4>
                        <p className="text-[13px] sm:text-[14px] leading-tight font-medium text-black whitespace-nowrap ml-auto text-right">
                          ₹ {itemPrice}
                        </p>
                      </div>
                      <p className="text-[12px] sm:text-[13px] font-medium text-black mt-1">
                        Quantity: {maxQuantity}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 w-full">
                      <button
                        type="button"
                        onClick={() => setStep("return")}
                        style={{
                          border: "1px solid #000",
                          boxSizing: "border-box",
                        }}
                        className="group mt-0 w-full text-black px-3 py-2 flex items-center justify-between text-sm sm:text-[15px] font-semibold tracking-wide"
                      >
                        <div className="relative h-5 overflow-hidden flex-1">
                          <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                            <span className="flex h-5 text-sm sm:text-base items-center">
                              Return
                            </span>
                            <span className="flex h-5 text-sm sm:text-base items-center">
                              Return
                            </span>
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setStep("exchange")}
                        style={{
                          border: "1px solid #000",
                          boxSizing: "border-box",
                        }}
                        className="group mt-0 w-full text-black px-3 py-2 flex items-center justify-between text-sm sm:text-[15px] font-semibold tracking-wide"
                      >
                        <div className="relative h-5 overflow-hidden flex-1">
                          <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                            <span className="flex h-5 text-sm sm:text-base items-center">
                              Exchange Size
                            </span>
                            <span className="flex h-5 text-sm sm:text-base items-center">
                              Exchange Size
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Return Step */}
            {step === "return" && (
              <>
                {/* Product Display */}
                <div>
                  <div className="flex gap-2 mb-4 pt-4">
                    <div className="w-32 h-40 sm:w-48 sm:h-48 bg-gray-100 flex-shrink-0">
                      {itemImage && (
                        <img
                          src={itemImage}
                          alt={itemName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex flex-col justify-between flex-1 min-w-0">
                      <div className="">
                        <div className="flex w-full items-start justify-between">
                          <h4 className="text-[13px] sm:text-[14px] leading-tight font-medium text-black pr-2">
                            {itemName} - {itemSize}
                          </h4>
                          <p className="text-[13px] sm:text-[14px] leading-tight font-medium text-black whitespace-nowrap">
                            ₹ {itemPrice}
                          </p>
                        </div>
                        <p className="text-[12px] sm:text-sm font-medium text-black mt-1">
                          Quantity: {maxQuantity}
                        </p>
                      </div>

                      <div className="">
                        <div className="inline-flex items-center border border-black h-8 mb-1">
                          <button
                            onClick={() =>
                              setReturnQuantity(Math.max(1, returnQuantity - 1))
                            }
                            className="w-7 h-full text-[16px] leading-none"
                            disabled={returnQuantity <= 1}
                          >
                            -
                          </button>
                          <span className="text-[12px] font-medium w-9 text-center">
                            {returnQuantity}
                          </span>
                          <button
                            onClick={() =>
                              setReturnQuantity(
                                Math.min(maxQuantity, returnQuantity + 1),
                              )
                            }
                            className="w-7 h-full text-[16px] leading-none"
                            disabled={returnQuantity >= maxQuantity}
                          >
                            +
                          </button>
                        </div>
                        <p className="text-[12px] font-medium text-black">
                          Select quantity for return
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quantity Selector */}

                <div className="mt-auto space-y-2 pb-1">
                  <select
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    className="w-full h-9 sm:h-8 font-bdogrotesk px-2 text-sm sm:text-base border border-black bg-transparent focus:outline-none"
                  >
                    <option value="">Tell us what went wrong?</option>
                    <option value="Wrong size">Wrong size</option>
                    <option value="Defective product">Defective product</option>
                    <option value="Not as described">Not as described</option>
                    <option value="Quality issues">Quality issues</option>
                    <option value="Ordered by mistake">
                      Ordered by mistake
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* COD Bank Details Form */}
                {isCODOrder && (
                  <div className="mt-4 pt-4 pb-4 border-t border-black space-y-3">
                    <p className="text-xs sm:text-sm font-medium text-black">Bank Account for Refund</p>

                    <input
                      type="text"
                      placeholder="Account Holder Name"
                      value={bankDetails.accountHolderName}
                      onChange={(e) => setBankDetails({...bankDetails, accountHolderName: e.target.value})}
                      className="w-full h-9 sm:h-8 font-bdogrotesk px-2 text-sm sm:text-base border border-black bg-transparent focus:outline-none"
                    />

                    <input
                      type="text"
                      placeholder="Account Number"
                      value={bankDetails.accountNumber}
                      onChange={(e) => setBankDetails({...bankDetails, accountNumber: e.target.value})}
                      className="w-full h-9 sm:h-8 font-bdogrotesk px-2 text-sm sm:text-base border border-black bg-transparent focus:outline-none"
                    />

                    <input
                      type="text"
                      placeholder="IFSC Code (e.g., SBIN0001234)"
                      value={bankDetails.ifscCode}
                      onChange={(e) => setBankDetails({...bankDetails, ifscCode: e.target.value})}
                      onBlur={handleVerifyBank}
                      className="w-full h-9 sm:h-8 font-bdogrotesk px-2 text-sm sm:text-base border border-black bg-transparent focus:outline-none"
                    />

                    {bankVerifying && (
                      <p className="text-xs text-gray-600">Verifying bank...</p>
                    )}

                    {bankDetails.bankName && !bankError && (
                      <p className="text-xs text-green-700 font-medium">✓ {bankDetails.bankName}</p>
                    )}

                    {bankError && (
                      <p className="text-xs text-red-600 font-medium">{bankError}</p>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleReturn}
                  disabled={loading || !returnReason || (isCODOrder && (!!bankError || !bankDetails.bankName))}
                  className="group mt-0 w-full bg-black text-white px-3 py-3 flex items-center justify-between text-sm sm:text-[15px] font-semibold tracking-wide box-border disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="relative h-5 overflow-hidden">
                    <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                      <span className="flex font-bdogrotesk h-5 items-center">
                        {loading ? "Processing..." : "Place Return Request"}
                      </span>
                      <span className="flex h-5 items-center">
                        {loading ? "Processing..." : "Place Return Request"}
                      </span>
                    </div>
                  </div>
                </button>
              </>
            )}

            {/* Exchange Step */}
            {step === "exchange" && (
              <>
                {/* Product Display */}
                <div>
                  <div className="flex gap-2 mb-4 pt-4">
                    <div className="w-32 h-40 sm:w-48 sm:h-48 bg-gray-100 flex-shrink-0">
                      {itemImage && (
                        <img
                          src={itemImage}
                          alt={itemName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex flex-col justify-between flex-1 min-w-0">
                      <div className="">
                        <div className="flex w-full items-start justify-between">
                          <h4 className="text-[13px] sm:text-[14px] leading-tight font-medium text-black pr-2">
                            {itemName} - {itemSize}
                          </h4>
                          <p className="text-[13px] sm:text-[14px] leading-tight font-medium text-black whitespace-nowrap">
                            ₹ {itemPrice}
                          </p>
                        </div>
                        <p className="text-[12px] sm:text-sm font-medium text-black mt-1">
                          Quantity: {maxQuantity}
                        </p>
                      </div>

                      {/* Size Selector */}
                      <div className="mb-3">
                        <div className="flex gap-2 sm:gap-3 mb-1 flex-wrap text-sm sm:text-base">
                          {availableSizes.map((size) => (
                            <button
                              key={size}
                              onClick={() => setExchangeSize(size)}
                              className={`${exchangeSize === size ? "text-black font-medium" : "text-gray-400"}`}
                            >
                              <span
                                className={
                                  exchangeSize === size
                                    ? "inline-block pb-1 border-b-2 border-black"
                                    : "inline-block pb-1 border-b-2 border-transparent"
                                }
                              >
                                {size}
                              </span>
                            </button>
                          ))}
                        </div>
                        <p className="text-[12px] font-medium text-black">
                          Select new size
                        </p>
                      </div>

                      {/* Quantity Selector */}
                      <div className="mb-3">
                        <div className="inline-flex items-center border border-black h-8 mb-1">
                          <button
                            onClick={() =>
                              setExchangeQuantity(Math.max(1, exchangeQuantity - 1))
                            }
                            className="w-7 h-full text-[16px] leading-none"
                            disabled={exchangeQuantity <= 1}
                          >
                            -
                          </button>
                          <span className="text-[12px] font-medium w-9 text-center">
                            {exchangeQuantity}
                          </span>
                          <button
                            onClick={() =>
                              setExchangeQuantity(
                                Math.min(maxQuantity, exchangeQuantity + 1),
                              )
                            }
                            className="w-7 h-full text-[16px] leading-none"
                            disabled={exchangeQuantity >= maxQuantity}
                          >
                            +
                          </button>
                        </div>
                        <p className="text-[12px] font-medium text-black">
                          Select quantity for exchange
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-auto space-y-2 pb-1">
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(true)}
                    style={{
                      border: "1px solid #000",
                      boxSizing: "border-box",
                    }}
                    className="group mt-0 w-full text-black px-3 py-2 flex items-center justify-between text-sm sm:text-[15px] font-semibold tracking-wide"
                  >
                    <div className="relative h-5 overflow-hidden flex-1">
                      <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                        <span className="flex h-5 text-sm sm:text-base items-center">
                          View Size Chart
                        </span>
                        <span className="flex h-5 text-sm sm:text-base items-center">
                          View Size Chart
                        </span>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={handleExchange}
                    disabled={loading || !exchangeSize}
                    className="group mt-0 w-full bg-black text-white px-3 py-3 flex items-center justify-between text-sm sm:text-[15px] font-semibold tracking-wide box-border disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="relative h-5 overflow-hidden">
                      <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                        <span className="flex font-bdogrotesk h-5 items-center">
                          {loading ? "Processing..." : "Place Size Exchange Request"}
                        </span>
                        <span className="flex h-5 items-center">
                          {loading ? "Processing..." : "Place Size Exchange Request"}
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showSizeGuide && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[1001]"
            onClick={() => setShowSizeGuide(false)}
          />
          <div
            className="fixed inset-0 z-[1002] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowSizeGuide(false)}
          >
            <div
              className="bg-white max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto font-bdogrotesk"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-8 md:py-6">
                <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6 md:mb-8 gap-3">
                  <h2 className="text-sm sm:text-base md:text-lg font-medium font-bdogrotesk pr-2 leading-tight">
                    Size chart for {isBottomForChart ? "bottoms" : "tops & dresses"} *
                  </h2>
                  <button
                    onClick={() => setShowSizeGuide(false)}
                    className="text-sm sm:text-base md:text-lg hover:text-gray-600 font-bdogrotesk font-medium flex-shrink-0"
                  >
                    Close
                  </button>
                </div>

                <div>
                  <div className="w-full overflow-x-auto">
                    <table className="min-w-[620px] w-full font-bdogrotesk text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                    <thead>
                      <tr className="border-b-2 border-black">
                        {sizeChartData.headers.map((h, idx) => (
                          <th
                            key={`chart-header-${idx}`}
                            className="px-1 sm:px-2 md:px-4 py-2 sm:py-3 md:py-4 text-left font-medium text-xs sm:text-sm md:text-base"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sizeChartData.rows.map((row, rIdx) => (
                        <tr key={`chart-row-${rIdx}`} className="border-b border-black">
                          <td className="px-1 sm:px-2 md:px-4 py-2 sm:py-3 md:py-4 text-left font-medium text-xs sm:text-sm md:text-base">
                            {row.label}
                          </td>
                          {row.values.map((val, cIdx) => (
                            <td
                              key={`chart-cell-${rIdx}-${cIdx}`}
                              className="px-1 sm:px-2 md:px-4 py-2 sm:py-3 md:py-4 text-left font-medium text-xs sm:text-sm md:text-base"
                            >
                              {val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg mt-4 sm:mt-6 font-bdogrotesk font-medium">
                    * All measurements are in Inches * All measurements (except length) are of body NOT garments
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReturnExchangeSidebar;
